import React, { Component } from "react";
import { Popper } from "react-popper";
import { I18n } from "react-redux-i18n";

import "../../../styles/components/utils/components/TooltipButton.scss";

// tooltip bottom margin, in px
const TOOLTIP_BOTTOM_MARGIN = 5;
const TOOLTIP_HIDE_DELAY_MS = 60;
let tooltipButtonInstanceCounter = 0;

class TooltipButton extends Component {
  constructor(props) {
    super(props);
    this._mounted = false;
    this.btnRef = null;
    this.hideTooltipTimer = null;
    this.tooltipInstanceId = `tooltip-button-${tooltipButtonInstanceCounter++}`;

    this.state = {
      showTooltip: false,
    };

    this.setShowTooltip = this.setShowTooltip.bind(this);
    this.getTooltipStyle = this.getTooltipStyle.bind(this);
  }

  componentDidMount() {
    this._mounted = true;
  }

  componentWillUnmount() {
    this._mounted = false;
    this.clearHideTooltipTimer();
  }

  setShowTooltip(showTooltip) {
    if (showTooltip) this.clearHideTooltipTimer();
    this.safeSetState({ showTooltip });
  }

  clearHideTooltipTimer() {
    if (this.hideTooltipTimer) {
      clearTimeout(this.hideTooltipTimer);
      this.hideTooltipTimer = null;
    }
  }

  scheduleHideTooltip() {
    this.clearHideTooltipTimer();
    this.hideTooltipTimer = setTimeout(() => {
      this.setShowTooltip(false);
      this.hideTooltipTimer = null;
    }, TOOLTIP_HIDE_DELAY_MS);
  }

  getTooltipStyle() {
    if (this.btnRef && typeof this.btnRef.getBoundingClientRect === "function") {
      const btnRect = this.btnRef.getBoundingClientRect();
      if (!btnRect) return {};
      const {
        top: btnTop = 0,
        left: btnLeft = 0,
        width: btnWidth = 0,
        height: btnHeight = 0,
      } = btnRect;
      const pxTemplate = value => `${value}px`;

      const popupRef = this.props.tooltipOffset?.();
      if (popupRef && popupRef.current && typeof popupRef.current.getBoundingClientRect === "function") {
        const popupRect = popupRef.current.getBoundingClientRect();
        if (!popupRect) return {};
        const {
          top: popupTop = 0,
          left: popupLeft = 0,
          width: popupWidth = 0,
          height: popupHeight = 0,
        } = popupRect;
        return {
          top: pxTemplate(btnTop - popupTop - TOOLTIP_BOTTOM_MARGIN),
          left: pxTemplate(btnLeft - popupLeft + btnWidth / 2),
        };
      } else {
        return {
          top: pxTemplate(btnTop - TOOLTIP_BOTTOM_MARGIN),
          left: pxTemplate(btnLeft + btnWidth / 2),
        };
      }
    }
    return {};
  }

  safeSetState(params, callback) {
    if (this._mounted) this.setState(params, callback);
  }

  getTooltipText(tooltip) {
    if (typeof tooltip === "string") return tooltip;
    if (typeof tooltip === "number") return `${tooltip}`;
    if (
      React.isValidElement(tooltip) &&
      tooltip.props &&
      typeof tooltip.props.value === "string"
    ) {
      return I18n.t(tooltip.props.value);
    }
    if (
      React.isValidElement(tooltip) &&
      tooltip.props &&
      typeof tooltip.props.children === "string"
    ) {
      return tooltip.props.children;
    }
    return undefined;
  }

  handleTooltipShow(event, handler) {
    if (handler) handler(event);
    this.clearHideTooltipTimer();
    this.setShowTooltip(true);
  }

  handleTooltipHide(event, handler) {
    if (handler) handler(event);
    this.scheduleHideTooltip();
  }

  renderTooltipButton() {
    const {
      tooltipRef,
      extended,
      tooltip,
      setRef,
      className,
      children,
      disabled,
      ariaLabel,
      onMouseEnter,
      onMouseLeave,
      onFocus,
      onBlur,
      title,
      ...btnProps
    } = this.props;
    const tooltipText = this.getTooltipText(tooltip);
    const fallbackLabel = ariaLabel || tooltipText;
    const fallbackTitle = title || tooltipText;
    const tooltipTriggerProps = {
      onMouseEnter: (event) => this.handleTooltipShow(event, onMouseEnter),
      onMouseLeave: (event) => this.handleTooltipHide(event, onMouseLeave),
      onFocus: (event) => this.handleTooltipShow(event, onFocus),
      onBlur: (event) => this.handleTooltipHide(event, onBlur),
    };
    const buttonElement = (
      <button
        ref={ref => {
          this.btnRef = ref;
          setRef?.(ref);
        }}
        className={`clear ${extended ? "extended" : ""} ${className}`}
        disabled={disabled}
        aria-label={btnProps["aria-label"] || fallbackLabel}
        title={btnProps.title || fallbackTitle}
        {...tooltipTriggerProps}
        {...btnProps}
      >
        {children}
        {extended && tooltip}
      </button>
    );

    if (disabled) {
      return (
        <span className="tooltip-button-trigger" {...tooltipTriggerProps}>
          {buttonElement}
        </span>
      );
    }

    return (
      buttonElement
    );
  }

  render() {
    const { tooltip, extended, tooltipInPopup, tooltipBoundaryElement } = this.props;
    const tooltipText = this.getTooltipText(tooltip);
    const tooltipKey = `${this.tooltipInstanceId}-${tooltipText || "tooltip"}`;
    const boundary = tooltipBoundaryElement || "clippingParents";
    const tooltipElement =
      !extended && this.state.showTooltip ? (
        tooltipInPopup ? (
          <Popper
            referenceElement={this.btnRef}
            placement="top"
            strategy="fixed"
            modifiers={[
              {
                name: "offset",
                options: {
                  offset: [0, TOOLTIP_BOTTOM_MARGIN],
                },
              },
              {
                name: "flip",
                options: {
                  fallbackPlacements: ["bottom", "right", "left"],
                  padding: 8,
                  rootBoundary: "viewport",
                },
              },
              {
                name: "preventOverflow",
                options: {
                  boundary,
                  rootBoundary: "viewport",
                  padding: 8,
                  altAxis: true,
                  tether: true,
                },
              },
            ]}
          >
            {({ ref, style, placement }) => (
              <div
                key={tooltipKey}
                ref={ref}
                style={{ ...style, zIndex: 10000 }}
                data-placement={placement}
                className="btn-tooltip"
              >
                <span>{tooltip}</span>
              </div>
            )}
          </Popper>
        ) : (
          <div key={tooltipKey} className="btn-tooltip" style={this.getTooltipStyle()}>
            <span>{tooltip}</span>
          </div>
        )
      ) : null;

    return (
      <div className="tooltip-button-container">
        {tooltipElement}
        {this.renderTooltipButton()}
      </div>
    );
  }
}

export default TooltipButton;
